import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  Flex,
  Card,
  CardHeader,
  CardBody,
  Text,
  Spinner,
  Center,
  useToast,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Grid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Stack,
  ButtonGroup,
  Input,
  useDisclosure,
  Icon
} from '@chakra-ui/react';
import { Line } from 'react-chartjs-2';
import { AddIcon, ViewIcon, CalendarIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { formatIndianCurrency } from '../utils/formatters';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Logout Error",
        description: error.message || "Failed to log out",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  };
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  
  // Date range state
  const [datePreset, setDatePreset] = useState('last30days');
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialRef = useRef();
  
  const [selectedUser, setSelectedUser] = useState('all');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  const datePresets = {
    today: {
      label: 'Today',
      start: moment().startOf('day'),
      end: moment().endOf('day')
    },
    yesterday: {
      label: 'Yesterday',
      start: moment().subtract(1, 'days').startOf('day'),
      end: moment().subtract(1, 'days').endOf('day')
    },
    last7days: {
      label: 'Last 7 Days',
      start: moment().subtract(6, 'days').startOf('day'),
      end: moment().endOf('day')
    },
    last30days: {
      label: 'Last 30 Days',
      start: moment().subtract(29, 'days').startOf('day'),
      end: moment().endOf('day')
    },
    thisWeek: {
      label: 'This Week',
      start: moment().startOf('week'),
      end: moment().endOf('week')
    },
    lastWeek: {
      label: 'Last Week',
      start: moment().subtract(1, 'week').startOf('week'),
      end: moment().subtract(1, 'week').endOf('week')
    },
    thisMonth: {
      label: 'This Month',
      start: moment().startOf('month'),
      end: moment().endOf('month')
    },
    lastMonth: {
      label: 'Last Month',
      start: moment().subtract(1, 'month').startOf('month'),
      end: moment().subtract(1, 'month').endOf('month')
    },
    thisQuarter: {
      label: 'This Quarter',
      start: moment().startOf('quarter'),
      end: moment().endOf('quarter')
    },
    lastQuarter: {
      label: 'Last Quarter',
      start: moment().subtract(1, 'quarter').startOf('quarter'),
      end: moment().subtract(1, 'quarter').endOf('quarter')
    },
    thisYear: {
      label: 'This Year',
      start: moment().startOf('year'),
      end: moment().endOf('year')
    },
    lastYear: {
      label: 'Last Year',
      start: moment().subtract(1, 'year').startOf('year'),
      end: moment().subtract(1, 'year').endOf('year')
    },
    custom: {
      label: 'Custom Range',
      start: null,
      end: null
    }
  };

  // Helper to apply a preset
  const applyDatePreset = (preset) => {
    if (preset === 'custom') {
      // Keep existing custom range if already set
      return;
    }
    
    const range = datePresets[preset];
    setStartDate(range.start.format('YYYY-MM-DD'));
    setEndDate(range.end.format('YYYY-MM-DD'));
    setDatePreset(preset);
    if (isOpen) onClose();
  };

  // Helper to apply custom range
  const applyCustomRange = () => {
    setDatePreset('custom');
    onClose();
  };

  const fetchUsers = async () => {
    try {
      const token = await user.getIdToken();
      const response = await axios.get(process.env.REACT_APP_API_URL + '/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsers(response.data.users);
    } catch (error) {
      toast({
        title: 'Error fetching users',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const start = moment(startDate).startOf('day');
      const end = moment(endDate).endOf('day');
      
      const token = await user.getIdToken();
      const response = await axios.get(process.env.REACT_APP_API_URL + '/api/analytics/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          userId: selectedUser === 'all' ? null : selectedUser
        }
      });

      setStats(response.data);
    } catch (error) {
      toast({
        title: 'Error fetching analytics',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [startDate, endDate, selectedUser]);

  const renderTrendsChart = () => {
    if (!stats?.callAnalytics?.callsPerDay) return null;

    // Create a map of dates for easy lookup
    const dateMap = new Map();
    
    // Add all dates from calls data
    stats.callAnalytics.callsPerDay.forEach(day => {
      const dateStr = day._id;
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, { 
          calls: 0, 
          leads: 0, 
          budget: 0 
        });
      }
      dateMap.get(dateStr).calls = day.count;
    });
    
    // Add all dates from leads data
    if (stats?.leadAnalytics?.leadsPerDay) {
      stats.leadAnalytics.leadsPerDay.forEach(day => {
        const dateStr = day._id;
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, { 
            calls: 0, 
            leads: 0, 
            budget: 0 
          });
        }
        dateMap.get(dateStr).leads = day.count;
        dateMap.get(dateStr).budget = day.budgetSum || 0;
      });
    }
    
    // Convert the map to sorted arrays for the chart
    const sortedDates = Array.from(dateMap.keys()).sort();
    const chartData = sortedDates.map(date => ({
      date,
      ...dateMap.get(date)
    }));
    
    // Format the data for Chart.js
    const data = {
      labels: chartData.map(day => moment(day.date).format('MMM D')),
      datasets: [
        {
          label: 'Calls',
          data: chartData.map(day => day.calls),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
          yAxisID: 'y'
        },
        {
          label: 'Leads',
          data: chartData.map(day => day.leads),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.1,
          yAxisID: 'y'
        },
        {
          label: 'Budget (₹)',
          data: chartData.map(day => day.budget / 100000), // Display in lakhs
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1,
          yAxisID: 'y1'
        }
      ]
    };
    
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.dataset.label === 'Budget (₹)') {
                return label + '₹' + formatIndianCurrency(context.parsed.y * 100000);
              }
              return label + context.parsed.y;
            }
          }
        },
        legend: {
          position: 'top',
        },
        title: {
          display: false
        }
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Count'
          },
          beginAtZero: true
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Budget (₹ in lakhs)'
          },
          beginAtZero: true,
          min: 0,
          max: 300, // Max of 3 crores (300 lakhs)
          ticks: {
            callback: function(value) {
              if (value >= 100) {
                return (value/100) + ' Cr';
              }
              return value + ' L';
            }
          },
          grid: {
            drawOnChartArea: false
          }
        }
      }
    };

    return (
      <Box p={4} bg="white" borderRadius="lg" shadow="sm" h="400px">
        <Heading size="md" mb={4}>Performance Trends</Heading>
        <Box h="320px">
          <Line data={data} options={options} />
        </Box>
      </Box>
    );
  };

  const renderUserCallsTable = () => {
    if (!stats?.callAnalytics?.callsByUserAndOutcome || stats.callAnalytics.callsByUserAndOutcome.length === 0) {
      return (
        <Card>
          <CardHeader>
            <Heading size="md">User Call Statistics</Heading>
          </CardHeader>
          <CardBody>
            <Text>No call data available for the selected period.</Text>
          </CardBody>
        </Card>
      );
    }

    // Extract all possible outcomes from the data
    const allOutcomes = new Set();
    stats.callAnalytics.callsByUserAndOutcome.forEach(userCalls => {
      userCalls.outcomes.forEach(outcome => {
        allOutcomes.add(outcome.outcome);
      });
    });
    
    const outcomesList = [...allOutcomes].sort();
    
    // Map outcomes to colors
    const outcomeColors = {
      'interested': 'green',
      'not_interested': 'red',
      'callback': 'blue',
      'not_answered': 'yellow',
      'wrong_number': 'orange',
      'invalid': 'gray',
    };
    
    // Format outcome labels
    const formatOutcome = (outcome) => {
      return outcome
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    return (
      <Card>
        <CardHeader>
          <Heading size="md">User Call Statistics</Heading>
        </CardHeader>
        <CardBody overflowX="auto" p={2}>
          <Table variant="simple" size="sm" colorScheme="blue">
            <Thead bg="blue.50">
              <Tr>
                <Th>User</Th>
                <Th isNumeric>Total</Th>
                {outcomesList.map(outcome => (
                  <Th key={outcome} isNumeric>
                    <Badge colorScheme={outcomeColors[outcome] || 'gray'}>
                      {formatOutcome(outcome)}
                    </Badge>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {stats.callAnalytics.callsByUserAndOutcome.map(userCalls => {
                // Create a map of outcome counts for this user
                const userOutcomes = {};
                userCalls.outcomes.forEach(outcome => {
                  userOutcomes[outcome.outcome] = outcome.count;
                });
                
                return (
                  <Tr key={userCalls._id}>
                    <Td>
                      <Text fontWeight="bold">{userCalls.userName || userCalls.email || 'Unknown'}</Text>
                    </Td>
                    <Td isNumeric fontWeight="bold">{userCalls.totalCalls}</Td>
                    {outcomesList.map(outcome => (
                      <Td key={outcome} isNumeric>
                        {userOutcomes[outcome] || 0}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    );
  };

  // Get the current date range display label
  const getDateRangeLabel = () => {
    if (datePreset !== 'custom') {
      return datePresets[datePreset].label;
    }
    
    // Format custom range
    const start = moment(startDate).format('MMM D, YYYY');
    const end = moment(endDate).format('MMM D, YYYY');
    return `${start} - ${end}`;
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="custom.greenGrey">
      <Box py={4}>
        <Flex justify="space-between" align="center" mb={6}>
          <HStack spacing={4}>
            <Box width="150px" height="40px">
              <img src="/logo.png" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <Heading size="lg">Analytics Dashboard</Heading>
          </HStack>
          <HStack spacing={4} pr={6}>
            <Button
              leftIcon={<ViewIcon />}
              colorScheme="teal"
              variant="outline"
              onClick={() => navigate('/sheets')}
            >
              Google Sheets
            </Button>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              variant="outline"
              onClick={() => navigate('/')}
            >
              Leads
            </Button>
            <Button
              onClick={handleLogout}
              colorScheme="red"
              size="md"
            >
              Logout
            </Button>
          </HStack>
        </Flex>

      <Container maxW="container.xl">
        <Flex justify="space-between" align="center" mb={6}>
        <Flex gap={4}>
          <Popover
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            initialFocusRef={initialRef}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button rightIcon={<CalendarIcon />} variant="outline">
                {getDateRangeLabel()}
              </Button>
            </PopoverTrigger>
            <PopoverContent width="300px" p={2}>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader fontWeight="bold">Select Date Range</PopoverHeader>
              <PopoverBody>
                <Stack spacing={3}>
                  <Stack spacing={1}>
                    <Text fontWeight="medium">Presets</Text>
                    <ButtonGroup size="sm" variant="outline" isAttached>
                      <Button 
                        onClick={() => applyDatePreset('today')}
                        colorScheme={datePreset === 'today' ? 'blue' : 'gray'}
                      >
                        Today
                      </Button>
                      <Button 
                        onClick={() => applyDatePreset('yesterday')}
                        colorScheme={datePreset === 'yesterday' ? 'blue' : 'gray'}
                      >
                        Yesterday
                      </Button>
                    </ButtonGroup>
                    
                    <ButtonGroup size="sm" variant="outline" isAttached>
                      <Button 
                        onClick={() => applyDatePreset('last7days')}
                        colorScheme={datePreset === 'last7days' ? 'blue' : 'gray'}
                      >
                        Last 7 Days
                      </Button>
                      <Button 
                        onClick={() => applyDatePreset('last30days')}
                        colorScheme={datePreset === 'last30days' ? 'blue' : 'gray'}
                      >
                        Last 30 Days
                      </Button>
                    </ButtonGroup>
                    
                    <ButtonGroup size="sm" variant="outline" isAttached>
                      <Button 
                        onClick={() => applyDatePreset('thisMonth')}
                        colorScheme={datePreset === 'thisMonth' ? 'blue' : 'gray'}
                      >
                        This Month
                      </Button>
                      <Button 
                        onClick={() => applyDatePreset('lastMonth')}
                        colorScheme={datePreset === 'lastMonth' ? 'blue' : 'gray'}
                      >
                        Last Month
                      </Button>
                    </ButtonGroup>
                    
                    <ButtonGroup size="sm" variant="outline" isAttached>
                      <Button 
                        onClick={() => applyDatePreset('thisQuarter')}
                        colorScheme={datePreset === 'thisQuarter' ? 'blue' : 'gray'}
                      >
                        This Quarter
                      </Button>
                      <Button 
                        onClick={() => applyDatePreset('lastQuarter')}
                        colorScheme={datePreset === 'lastQuarter' ? 'blue' : 'gray'}
                      >
                        Last Quarter
                      </Button>
                    </ButtonGroup>
                    
                    <ButtonGroup size="sm" variant="outline" isAttached>
                      <Button 
                        onClick={() => applyDatePreset('thisYear')}
                        colorScheme={datePreset === 'thisYear' ? 'blue' : 'gray'}
                      >
                        This Year
                      </Button>
                      <Button 
                        onClick={() => applyDatePreset('lastYear')}
                        colorScheme={datePreset === 'lastYear' ? 'blue' : 'gray'}
                      >
                        Last Year
                      </Button>
                    </ButtonGroup>
                  </Stack>
                  
                  <Box pt={2}>
                    <Text mb={2} fontWeight="medium">Custom Range</Text>
                    <Flex gap={2} mb={2}>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        size="sm"
                        ref={initialRef}
                      />
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="sm"
                      />
                    </Flex>
                    <Button 
                      size="sm" 
                      colorScheme="blue" 
                      width="full"
                      onClick={applyCustomRange}
                    >
                      Apply Custom Range
                    </Button>
                  </Box>
                </Stack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          
          <Select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            w="200px"
          >
            <option value="all">All Users</option>
            {users.map(user => (
              <option key={user.uid} value={user.uid}>
                {user.email}
              </option>
            ))}
          </Select>
        </Flex>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
          <Stat>
            <Card>
              <CardBody>
                <StatLabel>Total Leads</StatLabel>
                <StatNumber>{stats?.leadAnalytics?.totalLeads || 0}</StatNumber>
                <StatHelpText>In selected period</StatHelpText>
              </CardBody>
            </Card>
          </Stat>

          <Stat>
            <Card>
              <CardBody>
                <StatLabel>Total Calls</StatLabel>
                <StatNumber>{stats?.callAnalytics?.totalCalls || 0}</StatNumber>
                <StatHelpText>In selected period</StatHelpText>
              </CardBody>
            </Card>
          </Stat>
          
          <Stat>
            <Card>
              <CardBody>
                <StatLabel>Calls per Lead</StatLabel>
                <StatNumber>
                  {stats?.leadAnalytics?.totalLeads
                    ? (stats.callAnalytics.totalCalls / stats.leadAnalytics.totalLeads).toFixed(1)
                    : 0}
                </StatNumber>
                <StatHelpText>Average in period</StatHelpText>
              </CardBody>
            </Card>
          </Stat>

          <Stat>
            <Card>
              <CardBody>
                <StatLabel>Potential Budget</StatLabel>
                <StatNumber>
                  {stats?.leadAnalytics?.leadsByStageWithBudget
                    ? formatIndianCurrency(stats.leadAnalytics.leadsByStageWithBudget.reduce((sum, stage) => sum + (stage.totalBudget || 0), 0))
                    : '₹0'}
                </StatNumber>
                <StatHelpText>Active leads in period</StatHelpText>
              </CardBody>
            </Card>
          </Stat>
        </SimpleGrid>

        <Box mb={6}>
          {renderTrendsChart()}
        </Box>

        <Grid templateColumns={{ base: "1fr", md: "1fr 2fr" }} gap={6} mb={6}>
          <Card>
            <CardHeader>
              <Heading size="md">Calls by Outcome</Heading>
            </CardHeader>
            <CardBody>
              {stats?.callAnalytics?.callsByOutcome.map(outcome => (
                <Flex key={outcome._id} justify="space-between" mb={2}>
                  <Badge colorScheme={
                    outcome._id === 'interested' ? 'green' : 
                    outcome._id === 'not_interested' ? 'red' : 
                    outcome._id === 'callback' ? 'blue' : 
                    outcome._id === 'not_answered' ? 'yellow' : 
                    outcome._id === 'wrong_number' ? 'orange' : 'gray'
                  } px={2} py={1}>
                    {outcome._id ? outcome._id
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ') : 'No Outcome'}
                  </Badge>
                  <Text fontWeight="bold">{outcome.count}</Text>
                </Flex>
              ))}
            </CardBody>
          </Card>
          
          {/* User Call Statistics Table */}
          {renderUserCallsTable()}
        </Grid>
        
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
          <Card>
            <CardHeader>
              <Heading size="md">Leads by Stage</Heading>
            </CardHeader>
            <CardBody>
              {stats?.leadAnalytics?.leadsByStage.map(stage => (
                <Flex key={stage._id} justify="space-between" mb={2}>
                  <Text>{stage._id || 'Unassigned'}</Text>
                  <Text fontWeight="bold">{stage.count}</Text>
                </Flex>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Heading size="md">Budget by Stage</Heading>
            </CardHeader>
            <CardBody>
              {stats?.leadAnalytics?.leadsByStageWithBudget?.map(stage => (
                <Flex key={stage._id} justify="space-between" mb={2}>
                  <Text>{stage._id || 'Unassigned'}</Text>
                  <Text fontWeight="bold">
                    {formatIndianCurrency(stage.totalBudget || 0)}
                  </Text>
                </Flex>
              ))}
              <Flex justify="space-between" mt={4} pt={2} borderTop="1px solid" borderColor="gray.200">
                <Text fontWeight="bold">Potential Budget</Text>
                <Text fontWeight="bold">
                  {formatIndianCurrency(stats?.leadAnalytics?.leadsByStageWithBudget?.reduce((sum, stage) => sum + (stage.totalBudget || 0), 0) || 0)}
                </Text>
              </Flex>
            </CardBody>
          </Card>
        </SimpleGrid>
        
      </Container>
      </Box>
    </Box>
  );
};

export default AnalyticsPage;
